/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useParams, NavLink as RouterLink } from 'react-router-dom';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import {
  makeStyles,
  Drawer,
  Hidden,
  Button,
  Divider,
  IconButton,
  Box,
  SvgIcon,
} from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import {
  BarChart2 as BarChartIcon,
  MessageCircle as ChatIcon,
  HelpCircle as HelpIcon,
  CornerUpRight as BackIcon,
  Home as HomeIcon,
  Terminal as TerminalIcon,
  Users as UsersIcon,
  Sliders as SettingsIcon,
  Target as CodeIcon,
} from 'react-feather';
import NavBarItem from './NavBarItem';
import { useTranslation } from 'react-i18next';
import Account from '../TopBar/Account';
import Settings from '../TopBar/Settings';
import { ROLES } from 'src/constants/roles';

const useStyles = makeStyles(theme => ({
  NavBarItem: {
    display: 'flex',
    alignContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '10px',
    marginBottom: '0',
    boxSizing: 'border-box',
    width: '50px',
    height: '45px',
    borderRadius: '4px',
    borderColor: '#dddddd',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  NavBarItem2: {
    display: 'flex',
    alignContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '10px',
    marginBottom: '0',
    boxSizing: 'border-box',
    width: '50px',
    height: '35px',
    borderRadius: '4px',
    borderColor: '#dddddd',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  NavBarItemIcon: {
    fontSize: '1.4rem',
  },
  desktopDrawer: {
    paddingTop: 30,
    position: 'relative',
    width: 90,
    paddingLeft: 5,
    height: '100%',
    alignContent: 'center',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
  },
  mobibleDrawer: {
    paddingTop: 64,
    position: 'relative',
    width: 80,
    paddingLeft: 5,
    height: '100%',
    alignContent: 'center',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
  },
}));

function NavBar({ openMobile, onMobileClose }) {
  const { botId } = useParams();
  const workspaces = useSelector(
    state => state.account.user?.workspaces || []
  );
  const userRole = useSelector(state => state.account.user?.role);
  const hasMultipleWorkspaces = workspaces.length > 1;
  const workspaceId = workspaces[0]?.id;
  const exitPath = workspaceId
    ? hasMultipleWorkspaces
      ? '/workspaces'
      : `/admin/${workspaceId}/bots`
    : null;
  const classes = useStyles();
  const location = useLocation();
  const { t, i18n } = useTranslation(['studio', 'common']);
  const [lng, setLng] = useState(t('language', { ns: 'common' }));

  function changeToEnglish() {
    i18n.changeLanguage('en');
    setLng('en');
  }
  function changeToSpanish() {
    i18n.changeLanguage('es');
    setLng('es');
  }

  const itemList = [
    {
      title: t('navBar.Home'),
      href: `/studio/${botId}/home`,
      icon: HomeIcon,
    },
    // {
    //   title: 'Live Chat',
    //   href: `/studio/${botId}/livechat/new`,
    //   icon: ChatIcon
    // },
    {
      title: t('navBar.Support'),
      href: `/studio/${botId}/support/new`,
      icon: ChatIcon,
    },
    {
      title: t('navBar.Actions'),
      href: `/studio/${botId}/actions/new`,
      icon: TerminalIcon,
      allowedRoles: [ROLES.dev, ROLES.admin, ROLES.superAdmin],
    },
    {
      title: t('navBar.LLMPlayground'),
      href: `/studio/${botId}/llm-playground`,
      icon: CodeIcon,
      allowedRoles: [ROLES.dev, ROLES.admin, ROLES.superAdmin],
    },
    {
      title: t('navBar.Analytics'),
      href: `/studio/${botId}/analytics/`,
      icon: BarChartIcon,
      allowedRoles: [ROLES.admin, ROLES.superAdmin],
    },
    {
      title: t('navBar.Leads'),
      href: `/studio/${botId}/leads`,
      icon: UsersIcon,
    },
    {
      title: t('navBar.Settings'),
      href: `/studio/${botId}/settings`,
      icon: SettingsIcon,
    },
  ];

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }

    // eslint-disable-next-line
  }, [location.pathname]);
  const visibleItems = itemList.filter(item => {
    if (!item.allowedRoles || item.allowedRoles.length === 0) {
      return true;
    }
    return item.allowedRoles.includes(userRole);
  });

  const content = visibleItems.map(item => {
    return (
      <NavBarItem
        key={item.title}
        active={location.pathname.includes(item.title.toLowerCase())}
        title={item.title}
        href={item.href}
        icon={item.icon}
      />
    );
  });

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor='left'
          classes={{ paper: classes.mobibleDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant='temporary'
        >
          {content}
          <Divider />
          {exitPath && (
            <RouterLink
              className={classes.NavBarItem}
              style={{ textDecoration: 'none', transform: 'scaleX(-1)' }}
              to={exitPath}
            >
              <Tooltip
                title={t('exit', { ns: 'common' }) + ' ' + botId}
                placement='right'
                arrow
              >
                <IconButton>
                  <SvgIcon className={classes.NavBarItemIcon}>
                    <BackIcon />
                  </SvgIcon>
                </IconButton>
              </Tooltip>
            </RouterLink>
          )}
          <RouterLink
            className={classes.NavBarItem}
            style={{ textDecoration: 'none' }}
            to={'/docs'}
            target='_blank'
          >
            <Tooltip
              title={t('documentation', { ns: 'common' })}
              placement='right'
              arrow
            >
              <IconButton>
                <SvgIcon className={classes.NavBarItemIcon}>
                  <HelpIcon />
                </SvgIcon>
              </IconButton>
            </Tooltip>
          </RouterLink>
          <Box className={classes.NavBarItem2}>
            {lng === 'en' ? (
              <Button onClick={changeToSpanish}>En</Button>
            ) : (
              <Button onClick={changeToEnglish}>Es</Button>
            )}
          </Box>
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor='left'
          classes={{ paper: classes.desktopDrawer }}
          open
          variant='persistent'
        >
          <Account />
          <Divider style={{ marginTop: 20, marginBottom: '12px' }} />
          {content}
          <Divider style={{ marginTop: 20 }} />
          {exitPath && (
            <RouterLink
              className={classes.NavBarItem2}
              style={{ textDecoration: 'none', transform: 'scaleX(-1)' }}
              to={exitPath}
            >
              <Tooltip
                title={t('exit', { ns: 'common' }) + ' ' + botId}
                placement='right'
                arrow
              >
                <IconButton>
                  <SvgIcon fontSize='small' className={classes.NavBarItemIconS}>
                    <BackIcon style={{ fontSize: '12px' }} />
                  </SvgIcon>
                </IconButton>
              </Tooltip>
            </RouterLink>
          )}
          <RouterLink
            className={classes.NavBarItem2}
            style={{ textDecoration: 'none' }}
            to={'/docs'}
            target='_blank'
          >
            <Tooltip
              title={t('documentation', { ns: 'common' })}
              placement='right'
              arrow
            >
              <IconButton>
                <SvgIcon fontSize='small' className={classes.NavBarItemIconS}>
                  <HelpIcon />
                </SvgIcon>
              </IconButton>
            </Tooltip>
          </RouterLink>
          <Box className={classes.NavBarItem2}>
            <Settings />
          </Box>
          <Box className={classes.NavBarItem2}>
            {lng === 'en' ? (
              <Button onClick={changeToSpanish}>En</Button>
            ) : (
              <Button onClick={changeToEnglish}>Es</Button>
            )}
          </Box>
        </Drawer>
      </Hidden>
    </>
  );
}

export default NavBar;
